import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus, Briefcase, MapPin, Copy, X, Loader2, Trash2, ChevronRight, GripVertical, Sparkles, Save, LayoutTemplate, Code2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, copyToClipboard } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import AppLayout from "@/components/AppLayout";
import EmbedModal from "@/components/EmbedModal";

const empty = {
  title: "", years_experience: "", min_qualification: "", location: "",
  employment_type: "Full-time", description: "", custom_fields: [], status: "open",
};

export default function Jobs() {
  const { tenant, user } = useAuth();
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [templates, setTemplates] = useState({ builtin: [], custom: [] });
  const [aiKeywords, setAiKeywords] = useState("");
  const [aiGenerating, setAiGenerating] = useState(false);
  const [embedJob, setEmbedJob] = useState(null);

  const load = async () => {
    try { const { data } = await api.get("/jobs"); setJobs(data); }
    catch { toast.error("Failed to load jobs"); }
    finally { setLoading(false); }
  };
  const loadTemplates = async () => {
    try { const { data } = await api.get("/job-templates"); setTemplates(data); } catch {}
  };
  useEffect(() => { load(); loadTemplates(); }, []);

  useEffect(() => {
    const h = (e) => e.key === "Escape" && setShowModal(false);
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, []);

  const openNew = () => { setForm(empty); setEditId(null); setAiKeywords(""); setShowModal(true); };
  const openEdit = (job) => { setForm({ ...empty, ...job }); setEditId(job.id); setAiKeywords(""); setShowModal(true); };

  const applyTemplate = (tpl) => {
    if (!tpl) return;
    setForm({ ...empty, ...tpl.data });
    toast.success(`Loaded "${tpl.name}" template`);
  };

  const generateDescription = async () => {
    if (!form.title.trim()) { toast.error("Enter a job title first"); return; }
    setAiGenerating(true);
    try {
      const { data } = await api.post("/ai/generate-description", {
        title: form.title, keywords: aiKeywords,
        years_experience: form.years_experience, min_qualification: form.min_qualification,
      });
      setForm((f) => ({ ...f, description: data.description }));
      toast.success("Description generated");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setAiGenerating(false); }
  };

  const saveAsTemplate = async () => {
    const name = window.prompt("Save this job as a reusable template. Template name:", form.title);
    if (!name || !name.trim()) return;
    const { title, years_experience, min_qualification, location, employment_type, description, custom_fields, status } = form;
    try {
      await api.post("/job-templates", { name: name.trim(), data: { title, years_experience, min_qualification, location, employment_type, description, custom_fields, status } });
      toast.success("Saved as template");
      loadTemplates();
    } catch { toast.error("Failed to save template"); }
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editId) await api.put(`/jobs/${editId}`, form);
      else await api.post("/jobs", form);
      toast.success(editId ? "Job updated" : "Job created — generating interview questions");
      setShowModal(false);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const removeJob = async (id, title) => {
    if (!window.confirm(`Delete "${title}" and all its applications?`)) return;
    try { await api.delete(`/jobs/${id}`); toast.success("Job deleted"); load(); }
    catch { toast.error("Only tenant admins can delete jobs"); }
  };

  const copyLink = async (jobId) => {
    const ok = await copyToClipboard(`${window.location.origin}/apply/${jobId}`);
    ok ? toast.success("Apply link copied") : toast.error("Couldn't copy the link");
  };

  const addField = () => setForm({ ...form, custom_fields: [...form.custom_fields, { label: "", type: "text", required: false }] });
  const updField = (i, key, val) => {
    const cf = [...form.custom_fields]; cf[i] = { ...cf[i], [key]: val }; setForm({ ...form, custom_fields: cf });
  };
  const rmField = (i) => setForm({ ...form, custom_fields: form.custom_fields.filter((_, idx) => idx !== i) });

  return (
    <AppLayout>
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tighter">Jobs</h1>
          <p className="text-[#86868B] mt-1">Create roles and collect AI-scored applications.</p>
        </div>
        <div className="flex items-center gap-2">
          {tenant && (
            <button onClick={async () => { const ok = await copyToClipboard(`${window.location.origin}/careers/${tenant.slug}`); ok ? toast.success("Careers page link copied") : toast.error("Couldn't copy the link"); }}
              data-testid="copy-careers-page"
              className="bg-white border border-black/10 text-[#1D1D1F] rounded-full px-4 py-2.5 font-semibold text-sm hover:bg-gray-50 active:scale-95 transition-all flex items-center gap-2">
              <Copy size={16} /> Careers page
            </button>
          )}
          <button onClick={openNew} data-testid="add-job-btn"
            className="bg-[#007AFF] text-white rounded-full px-5 py-2.5 font-semibold text-sm hover:bg-[#0051FF] active:scale-95 transition-all flex items-center gap-2">
            <Plus size={18} /> New Job
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-[#86868B]">Loading…</div>
      ) : jobs.length === 0 ? (
        <div className="bg-white rounded-2xl border border-black/5 p-12 text-center">
          <Briefcase size={40} className="mx-auto text-[#C7C7CC] mb-3" strokeWidth={1.5} />
          <p className="font-semibold">No jobs yet</p>
          <p className="text-sm text-[#86868B]">Create a role to start receiving applications.</p>
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          {jobs.map((job, i) => (
            <motion.div key={job.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
              data-testid="job-card" onClick={() => navigate(`/jobs/${job.id}`)}
              className="group bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-black/5 p-5 hover:shadow-[0_20px_40px_rgb(0,0,0,0.08)] hover:-translate-y-0.5 transition-all duration-300 cursor-pointer flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-[#007AFF]/10 flex items-center justify-center shrink-0">
                <Briefcase size={22} className="text-[#007AFF]" strokeWidth={1.8} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-bold text-lg tracking-tight truncate">{job.title}</h3>
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${job.status === "open" ? "bg-[#34C759]/10 text-[#34C759]" : "bg-gray-100 text-[#86868B]"}`}>
                    {job.status.toUpperCase()}
                  </span>
                </div>
                <div className="flex items-center gap-3 text-sm text-[#86868B] mt-1 flex-wrap">
                  {job.location && <span className="flex items-center gap-1"><MapPin size={13} /> {job.location}</span>}
                  {job.years_experience && <span>{job.years_experience}</span>}
                  <span>{job.employment_type}</span>
                </div>
              </div>
              <div className="text-right shrink-0 flex items-center gap-4">
                <div>
                  <div className="font-extrabold text-xl tracking-tight">{job.application_count}</div>
                  <div className="text-[11px] text-[#86868B]">applicants{job.new_count > 0 ? ` · ${job.new_count} new` : ""}</div>
                </div>
                <div className="flex flex-col gap-1" onClick={(e) => e.stopPropagation()}>
                  <button onClick={() => copyLink(job.id)} data-testid="copy-apply-link" title="Copy apply link"
                    className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"><Copy size={14} /></button>
                  <button onClick={() => setEmbedJob(job)} data-testid="embed-job-btn" title="Get embed code"
                    className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"><Code2 size={14} /></button>
                  {user?.role === "tenant_admin" && (
                    <button onClick={() => removeJob(job.id, job.title)} data-testid="delete-job-btn" title="Delete"
                      className="w-8 h-8 rounded-full bg-gray-100 hover:bg-[#FF3B30]/10 text-[#86868B] hover:text-[#FF3B30] flex items-center justify-center transition-colors"><Trash2 size={14} /></button>
                  )}
                </div>
                <ChevronRight size={20} className="text-[#C7C7CC] group-hover:text-[#007AFF] transition-colors" />
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/30 backdrop-blur-sm px-5 py-8 overflow-y-auto" onClick={() => setShowModal(false)}>
          <motion.form initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} onClick={(e) => e.stopPropagation()} onSubmit={save}
            className="bg-white rounded-3xl shadow-xl border border-black/5 p-7 w-full max-w-2xl" data-testid="job-modal">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-xl font-extrabold tracking-tight">{editId ? "Edit Job" : "New Job"}</h2>
              <button type="button" onClick={() => setShowModal(false)} className="text-[#86868B] hover:text-[#1D1D1F]"><X size={20} /></button>
            </div>
            <div className="space-y-4">
              {!editId && (templates.builtin.length + templates.custom.length) > 0 && (
                <div className="bg-[#007AFF]/[0.04] border border-[#007AFF]/15 rounded-xl p-3">
                  <label className="text-xs font-bold uppercase tracking-wide text-[#007AFF] flex items-center gap-1.5"><LayoutTemplate size={13} /> Start from a template</label>
                  <select data-testid="template-select" defaultValue=""
                    onChange={(e) => { const all = [...templates.builtin, ...templates.custom]; applyTemplate(all.find((t) => t.id === e.target.value)); e.target.value = ""; }}
                    className="mt-1.5 w-full bg-white border border-black/10 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-[#007AFF]/30 outline-none transition-colors">
                    <option value="">Choose a template to pre-fill…</option>
                    <optgroup label="Starter templates">
                      {templates.builtin.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                    </optgroup>
                    {templates.custom.length > 0 && (
                      <optgroup label="Your templates">
                        {templates.custom.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                      </optgroup>
                    )}
                  </select>
                </div>
              )}
              <F label="Job title" value={form.title} onChange={(v) => setForm({ ...form, title: v })} testId="job-title" placeholder="Senior Frontend Engineer" required />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <F label="Years of experience" value={form.years_experience} onChange={(v) => setForm({ ...form, years_experience: v })} testId="job-exp" placeholder="3-5 years" />
                <F label="Location" value={form.location} onChange={(v) => setForm({ ...form, location: v })} testId="job-location" placeholder="Remote / Bangalore" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <F label="Minimum qualification" value={form.min_qualification} onChange={(v) => setForm({ ...form, min_qualification: v })} testId="job-qual" placeholder="B.Tech / equivalent" />
                <div>
                  <label className="text-sm font-semibold">Employment type</label>
                  <select value={form.employment_type} onChange={(e) => setForm({ ...form, employment_type: e.target.value })} data-testid="job-type"
                    className="mt-1 w-full bg-gray-50/70 border border-black/10 rounded-xl px-4 py-2.5 text-sm focus:bg-white focus:ring-2 focus:ring-[#007AFF]/30 outline-none transition-colors">
                    {["Full-time", "Part-time", "Contract", "Internship"].map((o) => <option key={o}>{o}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-sm font-semibold">Job description</label>
                  <button type="button" onClick={generateDescription} disabled={aiGenerating} data-testid="generate-description-btn"
                    className="text-xs font-bold text-[#007AFF] bg-[#007AFF]/[0.08] hover:bg-[#007AFF]/15 rounded-full px-3 py-1.5 flex items-center gap-1.5 transition-colors disabled:opacity-60">
                    {aiGenerating ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />} Generate with AI
                  </button>
                </div>
                <input value={aiKeywords} onChange={(e) => setAiKeywords(e.target.value)} data-testid="ai-keywords"
                  placeholder="Optional keywords for the AI, e.g. React, TypeScript, team lead"
                  className="w-full bg-[#007AFF]/[0.03] border border-[#007AFF]/15 rounded-xl px-4 py-2 text-sm focus:bg-white focus:ring-2 focus:ring-[#007AFF]/30 outline-none transition-colors mb-2" />
                <textarea required value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="job-description" rows={6}
                  placeholder="Describe the role, responsibilities and requirements — or tap 'Generate with AI'. The AI also uses this to score candidates."
                  className="w-full bg-gray-50/70 border border-black/10 rounded-xl px-4 py-3 text-sm focus:bg-white focus:ring-2 focus:ring-[#007AFF]/30 focus:border-[#007AFF] outline-none transition-colors resize-none" />
              </div>

              <div>
                <div className="flex items-center justify-between">
                  <label className="text-sm font-semibold">Custom application fields</label>
                  <button type="button" onClick={addField} data-testid="add-custom-field"
                    className="text-xs font-semibold text-[#007AFF] hover:underline flex items-center gap-1"><Plus size={14} /> Add field</button>
                </div>
                <div className="mt-2 space-y-2">
                  {form.custom_fields.map((f, i) => (
                    <div key={i} className="flex items-center gap-2 bg-[#F5F5F7] rounded-xl p-2">
                      <GripVertical size={16} className="text-[#C7C7CC]" />
                      <input value={f.label} onChange={(e) => updField(i, "label", e.target.value)} placeholder="Field label" data-testid={`custom-field-label-${i}`}
                        className="flex-1 bg-white border border-black/10 rounded-lg px-3 py-1.5 text-sm outline-none focus:ring-2 focus:ring-[#007AFF]/30" />
                      <select value={f.type} onChange={(e) => updField(i, "type", e.target.value)} data-testid={`custom-field-type-${i}`}
                        className="bg-white border border-black/10 rounded-lg px-2 py-1.5 text-sm outline-none">
                        <option value="text">Text</option><option value="textarea">Paragraph</option><option value="number">Number</option>
                      </select>
                      <label className="flex items-center gap-1 text-xs text-[#86868B]">
                        <input type="checkbox" checked={f.required} onChange={(e) => updField(i, "required", e.target.checked)} /> Req
                      </label>
                      <button type="button" onClick={() => rmField(i)} className="text-[#C7C7CC] hover:text-[#FF3B30]"><X size={16} /></button>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-sm font-semibold">Status</label>
                <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="job-status"
                  className="mt-1 w-full bg-gray-50/70 border border-black/10 rounded-xl px-4 py-2.5 text-sm focus:bg-white focus:ring-2 focus:ring-[#007AFF]/30 outline-none transition-colors">
                  <option value="open">Open — accepting applications</option>
                  <option value="closed">Closed</option>
                </select>
              </div>
            </div>
            <div className="mt-6 flex items-center gap-3">
              <button type="button" onClick={saveAsTemplate} data-testid="save-template-btn"
                className="shrink-0 bg-white border border-black/10 text-[#1D1D1F] rounded-full px-4 py-3 font-semibold text-sm hover:bg-gray-50 active:scale-95 transition-all flex items-center gap-2">
                <Save size={16} /> Save as template
              </button>
              <button type="submit" disabled={saving} data-testid="save-job-submit"
                className="flex-1 bg-[#007AFF] text-white rounded-full px-6 py-3 font-semibold text-sm hover:bg-[#0051FF] active:scale-[0.98] transition-all flex items-center justify-center gap-2 disabled:opacity-60">
                {saving && <Loader2 size={16} className="animate-spin" />} {editId ? "Save changes" : "Create job"}
              </button>
            </div>
          </motion.form>
        </div>
      )}
      {embedJob && (
        <EmbedModal
          title="Embed this job"
          description={`Embed the "${embedJob.title}" role on any website. Clicking it opens the application form in a new tab.`}
          path={`/embed/job/${embedJob.id}`}
          height={340}
          onClose={() => setEmbedJob(null)}
        />
      )}
    </AppLayout>
  );
}

function F({ label, value, onChange, type = "text", testId, placeholder, required }) {
  return (
    <div>
      <label className="text-sm font-semibold">{label}</label>
      <input type={type} required={required} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId} placeholder={placeholder}
        className="mt-1 w-full bg-gray-50/70 border border-black/10 rounded-xl px-4 py-2.5 text-sm focus:bg-white focus:ring-2 focus:ring-[#007AFF]/30 focus:border-[#007AFF] outline-none transition-colors" />
    </div>
  );
}
